import type { RegistrationTerm } from "@app/types/user.types";
import { FormEvent, useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  TextField,
} from "@mui/material";
import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BlankLayout } from "@app/components/layouts";
import { Helmet } from "@app/components/atoms";
import { useToken } from "@app/hooks/useToken";
import {
  countryCodeOptions,
} from "@app/constants/route.constants";
import {
  getRegistrationTerms,
  registerUser,
} from "@app/api/users/users.api";
import { SIGNUP_PROFILE_KEY } from "@app/helpers/auth/signup.helper";
import {
  buildRegistrationConsent,
  getRegistrationLocale,
  validProfileName,
} from "./signup-profile.helper";
import "../Login/Login.scss";
import "./SignupProfile.scss";

type Profile = { email: string; surname: string; givenName: string };
function readProfile(token?: string): Profile {
  try {
    // Claims are display hints only; the API must validate the token and identity.
    const segment = token?.split(".")[1];
    const claims = segment
      ? JSON.parse(
          new TextDecoder().decode(
            Uint8Array.from(
              atob(segment.replace(/-/g, "+").replace(/_/g, "/")),
              (c) => c.charCodeAt(0),
            ),
          ),
        )
      : {};
    return {
      email: claims.emails?.[0] || claims.email || "",
      surname: claims.family_name || "",
      givenName: claims.given_name || "",
    };
  } catch {
    return { email: "", surname: "", givenName: "" };
  }
}
export function SignupProfile() {
  const { getTokens } = useToken();
  const token = getTokens().accessToken;
  const { i18n } = useTranslation();
  const locale = getRegistrationLocale(i18n.language);
  const [profile, setProfile] = useState(() => readProfile(token));
  const [country, setCountry] = useState("");
  const [searchable, setSearchable] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [terms, setTerms] = useState<RegistrationTerm[]>(
    [],
  );
  const [termsLoading, setTermsLoading] = useState(false);
  const [termsError, setTermsError] = useState(false);
  const [retryTerms, setRetryTerms] = useState(0);
  const [accepted, setAccepted] = useState(false);
  const submitting = useRef(false);
  useEffect(() => {
    let active = true;
    // Reset consent whenever the document request changes (including locale).
    setTerms([]);
    setAccepted(false);
    setTermsError(false);
    if (!country || !token) return;
    setTermsLoading(true);
    getRegistrationTerms(country, token, locale)
      .then((data) => {
        if (!Array.isArray(data)) throw new Error("Invalid terms response");
        const selected = ["TOU", "PRIVACY_POLICY"].map(
          (type) =>
            data.find(
              (item) =>
                item.type === type && item.isoLocaleCode === locale,
            ) || data.find((item) => item.type === type),
        );
        if (
          selected.some(
            (item) =>
              !item?.touVersionMasterId ||
              !item?.pdfUrl ||
              !/^https?:\/\//i.test(item.pdfUrl),
          )
        )
          throw new Error("Missing terms");
        if (active)
          setTerms(selected as RegistrationTerm[]);
      })
      .catch(() => {
        if (active) setTermsError(true);
      })
      .finally(() => {
        if (active) setTermsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [country, token, locale, retryTerms]);
  if (!token) return <Navigate to="/login" replace />;
  const valid =
    validProfileName(profile.surname) &&
    validProfileName(profile.givenName) &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profile.email) &&
    profile.email.length <= 113 &&
    !!country &&
    terms.length === 2 &&
    accepted &&
    !termsLoading &&
    !termsError;
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!valid || submitting.current) return;
    submitting.current = true;
    setSaving(true);
    setError("");
    try {
      const response = await registerUser(
        {
          surname: profile.surname.trim(),
          givenName: profile.givenName.trim(),
          email: profile.email,
          isSearchableByEmail: searchable,
          residenceCountryCode: country,
          isoLocaleCode: locale,
          touAgreement: accepted,
          privacyPolicyAgreement: accepted,
          touPrivacy: buildRegistrationConsent(terms, locale),
        },
        token,
        locale,
      );
      if (!response.data?.data)
        throw new Error(
          "The server did not confirm registration. Please try again.",
        );
      sessionStorage.removeItem(SIGNUP_PROFILE_KEY);
      window.location.replace("/");
    } catch (err: any) {
      setError(
        err?.response?.data?.error?.localizedMessage ||
          err?.response?.data?.error?.message ||
          err.message ||
          "Registration failed. Please try again.",
      );
    } finally {
      submitting.current = false;
      setSaving(false);
    }
  };
  return (
    <BlankLayout>
      <Helmet title="Complete registration | Operation assist" />
      <div className="login-page signup-page">
        <div className="login-left-section">
          <div
            className="login-bg-image"
            style={{ backgroundImage: "url(/bg.jpg)" }}
          />
        </div>
        <div className="login-right-section">
          <div className="login-header-logo">
            <img src="/logo.png" alt="Komatsu" className="login-logo-img" />
          </div>
          <div className="signup-card">
            <p className="signup-eyebrow">Initial registration</p>
            <h1>Profile information</h1>
            <p className="signup-description">
              Complete your profile to get started with Operation assist.
            </p>
            <form onSubmit={submit}>
              <fieldset className="signup-fields" disabled={saving}>
                <div className="signup-names">
                  <TextField
                    className="input-field signup-field"
                    label="Last Name"
                    required
                    autoComplete="family-name"
                    value={profile.surname}
                    inputProps={{ maxLength: 64 }}
                    onChange={(e) =>
                      setProfile({ ...profile, surname: e.target.value })
                    }
                    error={
                      !!profile.surname && !validProfileName(profile.surname)
                    }
                    helperText={
                      profile.surname && !validProfileName(profile.surname)
                        ? "Enter 1–64 characters without emoji or angle brackets."
                        : " "
                    }
                  />
                  <TextField
                    className="input-field signup-field"
                    label="First Name"
                    required
                    autoComplete="given-name"
                    value={profile.givenName}
                    inputProps={{ maxLength: 64 }}
                    onChange={(e) =>
                      setProfile({ ...profile, givenName: e.target.value })
                    }
                    error={
                      !!profile.givenName &&
                      !validProfileName(profile.givenName)
                    }
                    helperText={
                      profile.givenName && !validProfileName(profile.givenName)
                        ? "Enter 1–64 characters without emoji or angle brackets."
                        : " "
                    }
                  />
                </div>
                <TextField
                  className="input-field signup-field"
                  fullWidth
                  required
                  label="Email address for searching"
                  value={profile.email}
                  InputProps={{ readOnly: true }}
                  InputLabelProps={{ shrink: true }}
                  helperText="Sign-in email address cannot be changed"
                />
                {!profile.email && (
                  <Alert severity="error">
                    Your account did not provide an email address. Enable the
                    email claim in the B2C user flow, then sign in again.
                  </Alert>
                )}
                <FormControlLabel
                  className="checkbox-field"
                  control={
                    <Checkbox
                      checked={searchable}
                      onChange={(e) => setSearchable(e.target.checked)}
                    />
                  }
                  label="Allow to search by email address"
                />
                <TextField
                  className="input-field select-field signup-field"
                  fullWidth
                  select
                  required
                  label="Residence"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                >
                  {countryCodeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
                {termsLoading && (
                  <p role="status">Loading terms and privacy policy...</p>
                )}
                {termsError && (
                  <Alert
                    severity="error"
                    action={
                      <Button
                        onClick={() => setRetryTerms((value) => value + 1)}
                      >
                        Retry
                      </Button>
                    }
                  >
                    Unable to load terms for your residence.
                  </Alert>
                )}
                {terms.length === 2 && (
                  <FormControlLabel
                    className="checkbox-field"
                    control={
                      <Checkbox
                        checked={accepted}
                        onChange={(e) => setAccepted(e.target.checked)}
                      />
                    }
                    label={
                      <span>
                        I agree to the{" "}
                        {terms.map((term, index) => (
                          <span key={term.type}>
                            {index > 0 && " and "}
                            <a
                              href={term.pdfUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {term.type === "TOU"
                                ? "Terms of Use"
                                : "Privacy Policy"}
                            </a>
                          </span>
                        ))}
                      </span>
                    }
                  />
                )}
                {error && <Alert severity="error">{error}</Alert>}
                <Button
                  className="signup-submit"
                  fullWidth
                  variant="contained"
                  type="submit"
                  disabled={!valid || saving}
                >
                  {saving
                    ? "Completing registration..."
                    : "Complete registration"}
                </Button>
                <Button fullWidth href="/login">
                  Back to sign in
                </Button>
              </fieldset>
            </form>
          </div>
        </div>
      </div>
    </BlankLayout>
  );
}
